import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';

function HelpPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I create an account?',
          a: 'Click the "Register Free" button on the homepage, enter your email and password, verify your email with the code sent to you, and then complete your profile with your personal information and ID verification documents.'
        },
        {
          q: 'Is registration really free?',
          a: 'Yes! Creating an account and browsing profiles is completely free. We also offer premium features for those who want enhanced visibility and additional tools.'
        },
        {
          q: 'Why do I need to verify my identity?',
          a: 'Identity verification ensures the safety and authenticity of all members on our platform. This helps create a trusted community and prevents fake profiles.'
        }
      ]
    },
    {
      category: 'Profile & Privacy',
      questions: [
        {
          q: 'Can I edit my profile after verification?',
          a: 'Yes, you can update most of your profile information at any time. However, changes to key identity information may require re-verification.'
        },
        {
          q: 'Who can see my profile?',
          a: 'Only verified members who meet your match criteria can view your full profile. You have control over your privacy settings.'
        },
        {
          q: 'How do I delete my account?',
          a: 'You can delete your account from Settings > Account > Delete Account. Please note this action is permanent and cannot be undone.'
        }
      ]
    },
    {
      category: 'Matching & Communication',
      questions: [
        {
          q: 'How does matching work?',
          a: 'Our matching algorithm considers your preferences for religion, location, education, and other criteria you specify. You can browse matches and send interest to profiles you like.'
        },
        {
          q: 'How do I contact someone I\'m interested in?',
          a: 'Send them an "Interest" request from their profile. If they accept, you can then exchange messages through our secure messaging system.'
        },
        {
          q: 'What if someone is bothering me?',
          a: 'You can block and report any user who makes you uncomfortable. Go to their profile and click "Block" or "Report". We take safety seriously and will investigate all reports.'
        }
      ]
    },
    {
      category: 'Verification Process',
      questions: [
        {
          q: 'How long does verification take?',
          a: 'Identity verification typically takes 24-48 hours. Our team reviews each submission carefully to ensure authenticity and safety.'
        },
        {
          q: 'What documents do I need for verification?',
          a: 'You need a valid government-issued ID (driver\'s license, passport, or provincial ID) and a selfie holding your ID. All documents must be clear and readable.'
        },
        {
          q: 'What happens if my verification is rejected?',
          a: 'If your verification is rejected, you\'ll receive an email explaining the reason. You can resubmit your documents after addressing the issues mentioned.'
        }
      ]
    },
    {
      category: 'Premium Features',
      questions: [
        {
          q: 'What does Premium membership include?',
          a: 'Premium members get unlimited messaging, advanced search filters, profile highlighting, and priority customer support. Check our pricing page for current offers.'
        },
        {
          q: 'Can I cancel my Premium subscription?',
          a: 'Yes, you can cancel anytime from Settings > Subscription. Your premium benefits will remain active until the end of your billing period.'
        },
        {
          q: 'Are there any special promotions?',
          a: 'Yes! The first 100 users get FREE lifetime Premium access. Check your account to see if you\'re eligible.'
        }
      ]
    },
    {
      category: 'Technical Support',
      questions: [
        {
          q: 'I\'m having trouble logging in, what should I do?',
          a: 'Try resetting your password using the "Forgot Password" link on the login page. If issues persist, contact our support team.'
        },
        {
          q: 'The website is not loading properly',
          a: 'Try clearing your browser cache, updating your browser to the latest version, or trying a different browser. If problems continue, contact support.'
        },
        {
          q: 'How do I report a technical issue?',
          a: 'Contact us at quboolme@gmail.com with: 1) Description of the issue, 2) Your browser and device information, 3) Screenshots if possible, 4) Steps to reproduce the issue. Our tech team responds within 24 hours.'
        }
      ]
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      item =>
        item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50">
      <div className="w-full px-2 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How Can We Help You?
          </h1>
          <p className="text-xl text-gray-600">
            Find answers to common questions or contact our support team
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-mint-500 focus:border-transparent text-lg"
            />
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>

          <div className="space-y-8">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No results found for "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-navy-500 hover:text-navy-600 font-medium"
                >
                  Clear search
                </button>
              </div>
            ) : (
              filteredFaqs.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="bg-red-100 text-navy-500 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">
                      {categoryIndex + 1}
                    </span>
                    {category.category}
                  </h3>
                  <div className="space-y-4 ml-11">
                    {category.questions.map((item, index) => {
                      const globalIndex = `${categoryIndex}-${index}`;
                      const isExpanded = expandedFaq === globalIndex;
                      return (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleFaq(globalIndex)}
                            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                          >
                            <span className="font-medium text-gray-900 pr-4">{item.q}</span>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                            )}
                          </button>
                          {isExpanded && (
                            <div className="px-4 pb-4 text-gray-600">
                              {item.a}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Still Need Help */}
        <div className="mt-12 text-center bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Still Need Help?
          </h3>
          <p className="text-gray-600 mb-6">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <a
            href="mailto:quboolme@gmail.com"
            className="inline-block bg-mint-500 hover:bg-mint-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors duration-200"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}

export default HelpPage;